import React, { useCallback, useEffect, useRef } from "react";
import { Circle, FeatureGroup, Polygon, Popup, useMap } from "react-leaflet";
import L from "leaflet";

import { useBookingsMapContext } from "../Context/BookingsMapContext";
import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";

import { ShapeTooltip } from "./ShapeTooltip";
import { FloorChildrenType } from "../../../Floors/lib/floorData";
import { BookingsMapPopUp } from "../BookingsMapMarkers/BookingsMapPopUp";
import { MapEntityModal } from "../BookingsFloorMap/BookingsFloorMap";

const SHAPE_LABEL_MARGIN_PX = 30;
const ATTRIBUTION_SPLITTER = "-innerGoGetIDSplitter-";

type ShapeLayer = L.Circle | L.Polygon;

const positionShapeTooltip = (
  layer: ShapeLayer,
  alignment: string,
  map: L.Map
): void => {
  const tooltip = layer.getTooltip();

  if (!tooltip) {
    return;
  }

  const bounds = layer.getBounds();
  const centerPoint = map.latLngToLayerPoint(bounds.getCenter());
  const northPoint = map.latLngToLayerPoint(bounds.getNorthWest());
  const southPoint = map.latLngToLayerPoint(bounds.getSouthWest());

  const targetPoint = centerPoint.clone();
  const boundsHeight = Math.abs(northPoint.y - southPoint.y);
  const proportionalMargin = boundsHeight * 0.2;
  const dynamicMargin = Math.min(SHAPE_LABEL_MARGIN_PX, proportionalMargin);

  if (alignment === "top") {
    targetPoint.y = centerPoint.y - dynamicMargin;
  } else if (alignment === "bottom") {
    targetPoint.y = centerPoint.y + dynamicMargin;
  }

  const targetLatLng = map.layerPointToLatLng(targetPoint);

  tooltip.setLatLng(targetLatLng);
};

const parseShapeAttribution = (attribution?: string) => {
  const [shapeId, shapeName = "", labelAlignment = "center"] =
    attribution?.split(ATTRIBUTION_SPLITTER) || [];

  return { shapeId, shapeName, labelAlignment };
};

export const BookingsMapShapes = ({
  circles,
  polygonal,
  showRoomMarkers,
  showTooltips,
  handleShapeClick,
  handleMapEntityReservePress,
  room,
  scaleFactor = 0.7,
  mapReady = false,
  hasContrastTooltips = false,
}: {
  circles: L.Circle<any>[];
  polygonal: L.Polygon<any>[];
  showRoomMarkers: boolean;
  showTooltips: boolean;
  handleShapeClick: (id: string) => void;
  handleMapEntityReservePress: ({
    id,
    type,
    modalType,
    busySlots,
  }: MapEntityModal) => void;
  room?: FloorChildrenType;
  scaleFactor?: number;
  mapReady?: boolean;
  hasContrastTooltips?: boolean;
}) => {
  const { user } = useAuthContext();
  const { setReserveLaterTime } = useBookingsMapContext();
  const circleRefs = useRef<Array<L.Circle | null>>([]);
  const polygonRefs = useRef<Array<L.Polygon | null>>([]);
  const map = useMap();
  const shouldAlignTooltips = !showRoomMarkers && showTooltips;

  // Align tooltips only after map is ready (bounds are set)
  // This matches the pattern used in FloorPlan's drawMarkers function
  useEffect(() => {
    if (!shouldAlignTooltips || !mapReady) {
      return;
    }

    circleRefs.current.length = circles.length;
    polygonRefs.current.length = polygonal.length;

    const circleAlignments = circles.map(
      (circle) =>
        parseShapeAttribution(circle.options.attribution).labelAlignment
    );

    circleRefs.current.forEach((circleLayer, index) => {
      const alignment = circleAlignments[index];
      if (circleLayer && ["top", "bottom"].includes(alignment)) {
        positionShapeTooltip(circleLayer, alignment, map);
      }
    });

    const polygonAlignments = polygonal.map(
      (polygon) =>
        parseShapeAttribution(polygon.options.attribution).labelAlignment
    );

    polygonRefs.current.forEach((polygonLayer, index) => {
      const alignment = polygonAlignments[index];
      if (polygonLayer && ["top", "bottom"].includes(alignment)) {
        positionShapeTooltip(polygonLayer, alignment, map);
      }
    });
  }, [circles, polygonal, shouldAlignTooltips, map, mapReady]);

  useEffect(() => {
    if (!room) return;

    // open the popup for the correct circle or polygon based on the `room` id
    const circleIndex = circles.findIndex((circle) => {
      const shapeId = circle.options.attribution?.split(
        "-innerGoGetIDSplitter-"
      )[0];
      return shapeId === room.id;
    });

    const polygonIndex = polygonal.findIndex((polygon) => {
      const shapeId = polygon.options.attribution?.split(
        "-innerGoGetIDSplitter-"
      )[0];
      return shapeId === room.id;
    });

    if (circleIndex !== -1 && circleRefs.current[circleIndex]) {
      circleRefs.current[circleIndex]!.openPopup();
    }

    if (polygonIndex !== -1 && polygonRefs.current[polygonIndex]) {
      polygonRefs.current[polygonIndex]!.openPopup();
    }
  }, [room, circles, polygonal]);

  return (
    <>
      <FeatureGroup>
        {circles.map((circle, index) => {
          const { shapeId, shapeName, labelAlignment } = parseShapeAttribution(
            circle.options.attribution
          );

          if (!shapeId) return null;

          return (
            <div key={shapeId}>
              <Circle
                ref={(el) => (circleRefs.current[index] = el)}
                center={circle.getLatLng()}
                radius={circle.getRadius()}
                color={circle.options.color}
                fillOpacity={circle.options.fillOpacity}
                fillColor={circle.options.fillColor}
                weight={2}
                eventHandlers={{
                  click: () => {
                    handleShapeClick(shapeId);
                  },
                }}
              >
                {!showRoomMarkers && showTooltips && (
                  <ShapeTooltip
                    name={shapeName}
                    scaleFactor={scaleFactor}
                    className={circle.options.className}
                    labelAlignment={labelAlignment}
                    hasContrastTooltips={hasContrastTooltips}
                  />
                )}

                {room && room.id === shapeId && (
                  <Popup>
                    <BookingsMapPopUp
                      id={room.id}
                      isBooked={room.isBooked || false}
                      isAssigned={false}
                      isBlocked={room.isBlocked || false}
                      type={"Room"}
                      name={room.name}
                      tags={room.tags}
                      busySlots={room.busySlots || []}
                      email={user?.email}
                      amenities={room.amenities}
                      numberOfSeats={room.numberOfSeats}
                      handleOpenModal={() =>
                        handleMapEntityReservePress({
                          id: room.id,
                          busySlots: room.busySlots || [],
                          type: "room",
                          modalType: room.isBooked ? "delete" : "add",
                          bookingWindow: room.bookingWindow ?? 180,
                        })
                      }
                      handleReserveLaterModal={() => {
                        if (room.isBooked && room.busySlots) {
                          setReserveLaterTime(
                            new Date(room.busySlots[0].endTime)
                          );
                        }

                        handleMapEntityReservePress({
                          id: room.id,
                          busySlots: room.busySlots || [],
                          type: "room",
                          modalType: "add",
                          bookingWindow: room.bookingWindow ?? 180,
                        });
                      }}
                    />
                  </Popup>
                )}
              </Circle>
            </div>
          );
        })}

        {polygonal.map((polygon, index) => {
          const { shapeId, shapeName, labelAlignment } = parseShapeAttribution(
            polygon.options.attribution
          );

          if (!shapeId) return null;

          return (
            <div key={shapeId}>
              <Polygon
                ref={(el) => (polygonRefs.current[index] = el)}
                positions={polygon.getLatLngs()}
                color={polygon.options.color}
                fillOpacity={polygon.options.fillOpacity}
                fillColor={polygon.options.fillColor}
                weight={2}
                eventHandlers={{
                  click: () => {
                    handleShapeClick(shapeId);
                  },
                }}
              >
                {!showRoomMarkers && showTooltips && (
                  <ShapeTooltip
                    name={shapeName}
                    scaleFactor={scaleFactor}
                    className={polygon.options.className}
                    labelAlignment={labelAlignment}
                    hasContrastTooltips={hasContrastTooltips}
                  />
                )}

                {room && room.id === shapeId && (
                  <Popup>
                    <BookingsMapPopUp
                      id={room.id}
                      isBooked={room.isBooked || false}
                      isAssigned={false}
                      isBlocked={room.isBlocked || false}
                      type={"Room"}
                      name={room.name}
                      tags={room.tags}
                      busySlots={room.busySlots || []}
                      email={user?.email}
                      amenities={room.amenities}
                      numberOfSeats={room.numberOfSeats}
                      handleOpenModal={() =>
                        handleMapEntityReservePress({
                          id: room.id,
                          busySlots: room.busySlots || [],
                          type: "room",
                          modalType: room.isBooked ? "delete" : "add",
                          bookingWindow: room.bookingWindow ?? 180,
                        })
                      }
                      handleReserveLaterModal={() => {
                        if (room.isBooked && room.busySlots) {
                          setReserveLaterTime(
                            new Date(room.busySlots[0].endTime)
                          );
                        }

                        handleMapEntityReservePress({
                          id: room.id,
                          busySlots: room.busySlots || [],
                          type: "room",
                          modalType: "add",
                          bookingWindow: room.bookingWindow ?? 180,
                        });
                      }}
                    />
                  </Popup>
                )}
              </Polygon>
            </div>
          );
        })}
      </FeatureGroup>
    </>
  );
};
